import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Observable,
  Subject,
  catchError,
  forkJoin,
  map,
  of,
  switchMap,
} from 'rxjs';
import { environment } from 'src/environments/environment';

const apiUrl = environment.apiUrl;
const reseau = environment.reseau;

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private http: HttpClient) { }

  //////////////////////////////////////////////////////////
  // Récupération des communes, voies, numéros et test    //
  // d'eligibilité                                        //
  //////////////////////////////////////////////////////////

  communes: any;
  communesChanges: Subject<any> = new Subject<any>();
  departements: any;
  departementsChanges: Subject<any> = new Subject<any>();

  getCommunes() {
    return this.http.get(`${apiUrl}/${reseau}/communes`).pipe(
      map((res: any) => {
        this.communes = res.data;
        this.communesChanges.next(res.data);

        this.departements = res.departments;
        this.departementsChanges.next(res.departments);
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getCommunesSansJWT() {
    return this.http.get(`${apiUrl}/${reseau}/cities`).pipe(
      map((res: any) => {
        this.communes = res.data;
        this.communesChanges.next(res.data);

        this.departements = res.departments;
        this.departementsChanges.next(res.departments);
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getVoies(insee: string) {
    return this.http.get(`${apiUrl}/${reseau}/voies/${insee}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getNumeros(hexaclevoie: string, referenceVoieOI: string) {
    return this.http
      .get(
        `${apiUrl}/${reseau}/numeros/${hexaclevoie}?referenceVoieOI=${referenceVoieOI}`
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  getEligibilite(
    insee: any,
    referenceHexacleVoie: any,
    referenceHexacle: any,
    nomVoie: any,
    numeroVoie: any,
    complementNumeroVoie: any
  ) {
    return this.http
      .get(
        `${apiUrl}/${reseau}/eligibilite/${insee}?referenceHexacleVoie=${referenceHexacleVoie}&nomVoie=${nomVoie}&numeroVoie=${numeroVoie}&complementNumeroVoie=${complementNumeroVoie}&referenceHexacle=${referenceHexacle}`
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  getEligibiliteByIMB(
    insee: any,
    imb: any,
    voie?: any,
    numero?: any,
    referenceHexacleVoie?: any,
    referenceHexacle?: any
  ) {
    if (imb) {
      return this.http
        .get(
          `${apiUrl}/${reseau}/eligibilite/${insee}?identifiant_immeuble=${imb}`
        )
        .pipe(
          map((res: any) => {
            return res.data;
          }),
          catchError((error: any) => {
            throw new Error(error.message);
          })
        );
    } else {
      return this.http
        .get(
          `${apiUrl}/${reseau}/eligibilite/${insee}?referenceHexacleVoie=${referenceHexacleVoie}&nomVoie=${voie}&numeroVoie=${numero}&referenceHexacle=${referenceHexacle}`
        )
        .pipe(
          map((res: any) => {
            return res.data;
          }),
          catchError((error: any) => {
            throw new Error(error.message);
          })
        );
    }
  }

  async returnCurrentCommune(insee: any) {
    if (this.communes) {
      return this.communes.find((commune: any) => commune.insee === insee);
    } else {
      return new Promise((resolve) => {
        const subscription = this.communesChanges.subscribe((communes: any) => {
          const foundCommune = communes.find(
            (commune: any) => commune.insee === insee
          );
          resolve(foundCommune);
          subscription.unsubscribe(); // Assurez-vous de désabonner après avoir trouvé la commune
        });
      });
    }
  }

  async returnCurrentCommuneByID(id: any, first?: boolean) {
    if (this.communes) {
      return this.communes.find((commune: any) => commune.id === id);
    } else {
      return new Promise((resolve) => {
        if (first) this.getCommunes().subscribe();

        const subscription = this.communesChanges.subscribe((communes: any) => {
          const foundCommune = communes.find(
            (commune: any) => commune.id === id
          );
          resolve(foundCommune);
          subscription.unsubscribe(); // Assurez-vous de désabonner après avoir trouvé la commune
        });
      });
    }
  }

  //////////////////////////////////////////////////////////
  // Permet de récupérer les données du dashboard d'une   //
  // commune.                                             //
  //////////////////////////////////////////////////////////

  getInfos(insee: string) {
    return this.http.get(`${apiUrl}/${reseau}/exploit/communes/${insee}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getMairie(insee: string) {
    const cleaned = insee.replace(/^0+/, '');
    return this.http.get(`${apiUrl}/mairies?insee_eq=${cleaned}`).pipe(
      map((res: any) => {
        return res.data && res.data.length ? res.data[0] : null;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getCityActionTypes() {
    return this.http.get(`${apiUrl}/city-action-types`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  postCityActionType(name: string) {
    return this.http.post(`${apiUrl}/city-action-types/?name=${encodeURIComponent(name)}`, {}).pipe(
      map((res: any) => res),
      catchError((error: any) => { throw new Error(error.message); })
    );
  }

  patchCityActionType(id: number, name: string) {
    return this.http.patch(`${apiUrl}/city-action-types/${id}?name=${encodeURIComponent(name)}`, {}).pipe(
      map((res: any) => res),
      catchError((error: any) => { throw new Error(error.message); })
    );
  }

  deleteCityActionType(id: number) {
    return this.http.delete(`${apiUrl}/city-action-types/${id}`).pipe(
      map((res: any) => res),
      catchError((error: any) => { throw new Error(error.message); })
    );
  }

  postCityAction(cityId: number, action: any) {
    let params = `date=${action.date}&city_action_type_id=${action.city_action_type_id}&city_id=${cityId}`;
    (action.city_ids || []).forEach((id: number) => {
      params += `&city_ids[]=${id}`;
    });
    return this.http.post(`${apiUrl}/city-actions?${params}`, {}).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  postCityActionByInsees(insees: string[], action: any) {
    let params = `date=${action.date}&city_action_type_id=${action.city_action_type_id}`;
    if (insees.length === 1) {
      params += `&insee=${insees[0]}`;
    } else {
      insees.forEach((code: string) => {
        params += `&insees[]=${code}`;
      });
    }
    return this.http.post(`${apiUrl}/city-actions?${params}`, {}).pipe(
      map((res: any) => res),
      catchError((error: any) => { throw new Error(error.message); })
    );
  }

  getCityActionsByCity(cityId: number) {
    return this.http.get(`${apiUrl}/city-actions?city_id=${cityId}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  updateMairieHoraires(insee: string, horaires: any) {
    const cleaned = insee.replace(/^0+/, '');
    return this.http.patch(`${apiUrl}/mairies/${cleaned}`, horaires).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getMairies(page: number = 1, perPage: number = 50, filters: any = {}, sort: any = {}) {
    let params = `page=${page}&per_page=${perPage}`;
    Object.entries(filters).forEach(([key, value]) => {
      if (value && String(value).trim() !== '') {
        params += `&${key}=${encodeURIComponent(String(value).trim())}`;
      }
    });
    Object.entries(sort).forEach(([key, value]) => {
      if (value) {
        params += `&${key}=${encodeURIComponent(String(value))}`;
      }
    });
    return this.http.get(`${apiUrl}/mairies?${params}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  exportMairiesXls(filters: any = {}, selectedIds: number[] = []) {
    let params = `export_xls=1&page=1&per_page=9999`;
    Object.entries(filters).forEach(([key, value]) => {
      if (value && String(value).trim() !== '') {
        params += `&${key}=${encodeURIComponent(String(value).trim())}`;
      }
    });
    if (selectedIds.length > 0) {
      params += `&ids=${selectedIds.join(',')}`;
    }
    return this.http.get(`${apiUrl}/mairies?${params}`, {
      responseType: 'blob' as const
    });
  }

  //////////////////////////////////////////////////////////
  // Permet de récupérer les sections et champs des       //
  // fiches découvertes                                   //
  //////////////////////////////////////////////////////////

  getSections() {
    return this.http.get(`${apiUrl}/sections`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  postForm(contact_elu_id: any, city_id: any, answers: any) {
    const formData = new FormData();
    formData.append('crm_dashboard_contact_elu_id', contact_elu_id);
    formData.append('city_id', city_id);

    for (let i = 0; i < answers.length; i++) {
      formData.append(`answers[]`, JSON.stringify(answers[i]));
    }

    return this.http.post(`${apiUrl}/forms`, formData).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getCityForms(city_id: any) {
    return this.http.get(`${apiUrl}/cities/${city_id}/forms`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  editForm(contact_elu_id: any, city_id: any, form_id: any, answers: any) {
    const formData = new FormData();
    formData.append('crm_dashboard_contact_elu_id', contact_elu_id);
    formData.append('city_id', city_id);

    for (let i = 0; i < answers.length; i++) {
      formData.append(`answers[]`, JSON.stringify(answers[i]));
    }

    return this.http
      .post(`${apiUrl}/forms/${form_id}?_method=PUT`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  getActions() {
    return this.http.get(`${apiUrl}/actions`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getCityActions(city_id: any) {
    return this.http.get(`${apiUrl}/cities/${city_id}/actions`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  // postActionSchedule(action_id: any, city_id: any, plan_for: any, contact_elu_id: any) {
  postActionSchedule(action: any) {
    const formData = new FormData();
    formData.append(
      'crm_dashboard_v2_fiche_decouverte_action_id',
      action.action_id
    );
    formData.append('city_id', action.city_id);
    formData.append('plan_for', action.plan_for);
    formData.append('crm_dashboard_contact_elu_id', action.contact_elu_id);

    return this.http.post(`${apiUrl}/actions/schedule`, formData).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getSalonCityForms(insee: any) {
    return this.http.get(`${apiUrl}/cities/${insee}/forms`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // Toujours sur le dashboard, permet de récupérer les   //
  // infos d'un dossier en particulier                    //
  //////////////////////////////////////////////////////////

  getDossierInfos(num_dossier: string, client_mail: string) {
    return this.http
      .get(
        `${apiUrl}/${reseau}/exploit/dossier/${num_dossier}?client_mail=${client_mail}`
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  //////////////////////////////////////////////////////////
  // Gestion des contacts (élu et sous-élus) d'une        //
  // commune                                              //
  //////////////////////////////////////////////////////////

  createElu(infos: any) {
    const formData = new FormData();
    formData.append('nom', infos.nom);
    formData.append('prenom', infos.prenom);
    formData.append('email', infos.email);
    formData.append('login', infos.login);
    formData.append('password', infos.password);

    return this.http
      .post(`${apiUrl}/communes/${infos.insee}/elu`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  getContacts(insee: string) {
    return this.http.get(`${apiUrl}/communes/${insee}/contacts`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  addContact(insee: string, contact: any) {
    const formData = new FormData();
    formData.append('name', contact.name == null ? '' : contact.name);
    formData.append(
      'firstname',
      contact.firstname == null ? '' : contact.firstname
    );
    formData.append('phone1', contact.phone1 == null ? '' : contact.phone1);
    formData.append('phone2', contact.phone2 == null ? '' : contact.phone2);
    formData.append('title', contact.title == null ? '' : contact.title);
    formData.append(
      'title_2',
      // le HTML bind newContact.title_2 (2 marques) mais certains
      // objets API historiques exposent title2 — prendre ce qui existe
      contact.title_2 == null
        ? contact.title2 == null
          ? ''
          : contact.title2
        : contact.title_2
    );
    formData.append('email', contact.email == null ? '' : contact.email);
    formData.append(
      'email_2',
      contact.email_2 == null
        ? contact.email2 == null
          ? ''
          : contact.email2
        : contact.email_2
    );

    return this.http
      .post(`${apiUrl}/communes/${insee}/contacts`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  editContact(contact: any) {
    const formData = new FormData();
    formData.append('name', contact.name == null ? '' : contact.name);
    formData.append(
      'firstname',
      contact.firstname == null ? '' : contact.firstname
    );
    formData.append('phone1', contact.phone1 == null ? '' : contact.phone1);
    formData.append('phone2', contact.phone2 == null ? '' : contact.phone2);
    formData.append('title', contact.title == null ? '' : contact.title);
    formData.append(
      'title_2',
      // le HTML bind newContact.title_2 (2 marques) mais certains
      // objets API historiques exposent title2 — prendre ce qui existe
      contact.title_2 == null
        ? contact.title2 == null
          ? ''
          : contact.title2
        : contact.title_2
    );
    formData.append('email', contact.email == null ? '' : contact.email);
    formData.append(
      'email_2',
      contact.email_2 == null
        ? contact.email2 == null
          ? ''
          : contact.email2
        : contact.email_2
    );

    return this.http
      .post(`${apiUrl}/contacts/${contact.id}?_method=PUT`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  updateElu(elu: any) {
    const formData = new FormData();
    formData.append('nom', elu.nom);
    formData.append('prenom', elu.prenom);
    formData.append('email', elu.email);
    // email_proconnect : champ ProConnect (Losange uniquement) — ne pas
    // envoyer la chaîne "undefined" quand le champ n'existe pas (Rosace)
    if (elu.email_proconnect) {
      formData.append('email_proconnect', elu.email_proconnect);
    }

    return this.http
      .post(`${apiUrl}/elus/${elu.id}?_method=PUT`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  updateEluPasswordAsRegion(elu_id: any, newPassword: any) {
    const formData = new FormData();
    formData.append('password', newPassword);

    return this.http
      .post(`${apiUrl}/elus/${elu_id}?_method=PUT`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  addElu(city_id: any, elu: any) {
    const formData = new FormData();
    formData.append('login', elu.login);
    formData.append('nom', elu.nom);
    formData.append('prenom', elu.prenom);
    formData.append('email', elu.email);
    formData.append('dummy', elu.dummy);
    formData.append('nom_application', elu.nom_application);
    formData.append('insee', elu.insee);
    formData.append('password', elu.password);
    formData.append('civilite', elu.civilite);
    formData.append('organisme', elu.organisme);
    formData.append('adresse', elu.adresse);
    formData.append('cp', elu.cp);
    formData.append('ville', elu.ville);
    formData.append('telephone', elu.telephone);

    return this.http.post(`${apiUrl}/cities/${city_id}/elus`, formData).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getElus(city_id: any) {
    return this.http.get(`${apiUrl}/cities/${city_id}/elus`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getSingleElu(elu_id: any) {
    return this.http.get(`${apiUrl}/elus/${elu_id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }


  getEluToken(callback_token: any) {
    return this.http
      .post(`${apiUrl}/login/get_elu_token`, {
        callback_token: callback_token,
      })
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  updateEluPassword(elu_id: any, current_password: any, new_password: any) {
    const formData = new FormData();
    formData.append('current_password', current_password);
    formData.append('new_password', new_password);

    return this.http
      .post(`${apiUrl}/elus/${elu_id}/update_password?_method=PUT`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  deleteAccount(identifiant: any, password: any) {
    const formData = new FormData();
    formData.append('login', identifiant);
    formData.append('password', password);

    return this.http
      .post(`${apiUrl}/${reseau}/user?_method=DELETE`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  //////////////////////////////////////////////////////////
  // Permet de récupérer la Home d'une commune.           //
  // Le meme call est utilisé pour récupérer les contacts //
  // et les RS Losange/Rosace                             //
  //////////////////////////////////////////////////////////

  linksRS: any;
  linksRSChanges: Subject<any> = new Subject<any>();
  currentHome: any;
  currentHomeChanges: Subject<any> = new Subject<any>();

  getHome(insee: any) {
    return this.http.get(`${apiUrl}/${reseau}/communes/${insee}/home`).pipe(
      map((res: any) => {
        // if (!this.currentHome) {
        this.currentHome = res.data[0];
        this.currentHomeChanges.next(this.currentHome);
        this.linksRS = {
          facebook: res.data[0].facebook,
          twitter: res.data[0].twitter,
          linkedin: res.data[0].linkedin,
          instagram: res.data[0].instagram,
        };
        this.linksRSChanges.next(this.linksRS);
        // }

        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // Permet de récupérer la actualiés récentes d'une      //
  // commune.                                             //
  //////////////////////////////////////////////////////////

  getFeedItems() {
    return this.http.get(`${apiUrl}/${reseau}/feeditems`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getFeedItemsPublic() {
    return this.http.get(`${apiUrl}/${reseau}/feeditems_public`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // Permet de récupérer le contenu de la page            //
  // Documentations                                       //
  //////////////////////////////////////////////////////////

  getDocumentationPage() {
    return this.http
      .get(
        `${apiUrl}/${reseau}/page?page_id=${environment.pageDocumentationId}`
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  //////////////////////////////////////////////////////////
  // Permet de récupérer le contenu de la page            //
  // Informations                                         //
  //////////////////////////////////////////////////////////

  getInformationsPage() {
    return this.http
      .get(`${apiUrl}/${reseau}/page?page_id=${environment.pageInformationId}`)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  //////////////////////////////////////////////////////////
  // Vidéos                                               //
  //////////////////////////////////////////////////////////

  getVideosPage() {
    return this.http
      .get(`${apiUrl}/${reseau}/page?page_id=${environment.pageVideoId}`)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  //////////////////////////////////////////////////////////
  // Permet de récupérer les SRO à afficher sur la carte  //
  // d'éligibilité                                        //
  //////////////////////////////////////////////////////////

  getShape(insee: any) {
    let formattedData = {
      sros: [],
      houses: [],
      statut: null,
    };

    return this.http.get(`${apiUrl}/${reseau}/shape?insee=${insee}`).pipe(
      switchMap((res: any) => {
        formattedData.sros = this.filterSros(res.data[0].geoloc);
        formattedData.statut = res.data[0].statut;

        // Create an array of observables for the houses requests
        const houseRequestsObservables: Observable<any[]>[] =
          formattedData.sros.map((sro: any) =>
            this.getSROHouses(insee, sro.code_sro).pipe(
              catchError((error) => {
                console.error(
                  `Error fetching houses for SRO ${sro.code_sro}:`,
                  error
                );
                return of([]); // Return an empty array in case of error
              })
            )
          );

        if (houseRequestsObservables.length) {
          // Use forkJoin to wait for all house requests to complete
          return forkJoin(houseRequestsObservables).pipe(
            map((housesArray: any[]) => {
              // Flatten the array of house arrays into a single array
              formattedData.houses = [].concat(...housesArray);
              return formattedData;
            })
          );
        } else {
          return of(formattedData);
        }
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getSROHouses(insee: any, code_sro: any) {
    return (
      this.http
        // Endpoint des habitations par SRO : srov3 chez Losange, srov2 chez
        // Rosace (leur API n'expose pas srov3 — 404)
        .get(
          `${apiUrl}/${reseau}/communes/${insee}/${
            reseau === 'losange' ? 'srov3' : 'srov2'
          }?code_sro=${code_sro}`
        )
        .pipe(
          map((res: any) => {
            // res.data.forEach((house: any) => {
            //   if (house.IdentifiantImmeuble	== 'IMB-54314-AAEFV'){
            //     console.log(house);
            //   }
            // });
            return res.data;
          }),
          catchError((error: any) => {
            throw new Error(error.message);
          })
        )
    );
  }

  private filterSros(sros: any) {
    return sros.filter((sro: any) => {
      return (
        (sro.statut_elig_sro == 1 ||
          sro.statut_elig_sro == 2 ||
          sro.statut_elig_sro == 3) &&
        sro.code_sro &&
        sro.shp &&
        sro.shp.length
      );
    });
  }

  //////////////////////////////////////////////////////////
  // Pour la carte des travaux                            //
  //////////////////////////////////////////////////////////

  getMapWithCoords(coords: any) {
    return this.http
      .get(
        `${apiUrl}/${reseau}/coord?latitude_start=${coords.lat_start}&latitude_end=${coords.lat_end}&longitude_start=${coords.long_start}&longitude_end=${coords.long_end}`
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  //////////////////////////////////////////////////////////
  // Pour la carte des raccordements                      //
  //////////////////////////////////////////////////////////

  getMapWithCoordsRacc(coords: any) {
    return this.http
      .get(
        `${apiUrl}/${reseau}/get_map_racco?min_lat=${coords.lat_start}&max_lat=${coords.lat_end}&min_lon=${coords.long_start}&max_lon=${coords.long_end}`
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  //////////////////////////////////////////////////////////
  // Permet de récupérer les FAI d'une commune            //
  // PAS UTILISE DANS LE PROJET                           //
  //////////////////////////////////////////////////////////

  getFAIs(insee: any) {
    return this.http.get(`${apiUrl}/fai?insee=${insee}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // Gestion des articles                                 //
  //                                                      //
  //////////////////////////////////////////////////////////

  getArticles(city_id: any, page: any) {
    return this.http
      .get(`${apiUrl}/elu/cities/${city_id}/articles?page=${page}&per_page=10`)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  getSingleArticle(article_id: any) {
    return this.http.get(`${apiUrl}/elu/articles/${article_id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  postArticle(cities: any, article: any) {
    const formData = new FormData();
    formData.append('title', article.title);
    formData.append('sponsored', article.sponsored == true ? '1' : '0');
    formData.append('published_by', article.published_by);
    formData.append('published_at', article.published_at);

    // store in cities_id all the ids of the cities
    let cities_id: any = [];
    cities.forEach((city: any) => {
      cities_id.push(city.id);
    });

    cities_id.forEach((city_id: any) => {
      formData.append('cities_id[]', city_id);
    });

    return this.http.post(`${apiUrl}/article`, formData).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  editArticle(article: any) {
    const formData = new FormData();
    formData.append('title', article.title);
    formData.append('sponsored', article.sponsored == true ? '1' : '0');
    formData.append('published_at', article.published_at);

    return this.http
      .post(`${apiUrl}/articles/${article.id}?_method=PUT`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  linkCitiesToArticle(article_id: any, cities: any) {
    const formData = new FormData();
    cities.forEach((city: any) => {
      formData.append('cities_id[]', city.id);
    });

    return this.http
      .post(`${apiUrl}/articles/${article_id}/cities?_method=PUT`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  deleteArticle(article_id: any) {
    return this.http.delete(`${apiUrl}/articles/${article_id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  uploadArticleFile(file: any, order: any) {
    const formData = new FormData();
    formData.append('order', order);
    formData.append('file', file, file.name);

    return this.http.post(`${apiUrl}/articles_blocs/media`, formData).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  editArticleFile(file_id: any, order: any) {
    const formData = new FormData();
    formData.append('order', order);

    return this.http
      .put(`${apiUrl}/articles_blocs/media/${file_id}`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  deleteArticleFile(file_id: any) {
    return this.http.delete(`${apiUrl}/articles_blocs/media/${file_id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  createArticleBlocks(article_id: any, blocs: any) {
    const formData = new FormData();

    blocs.forEach((bloc: any) => {
      formData.append('blocs[]', JSON.stringify(bloc));
    });

    return this.http
      .post(`${apiUrl}/${article_id}/articles_blocs`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  updateArticleBlocks(blocs: any) {
    const formData = new FormData();
    blocs.forEach((bloc: any) => {
      const { id, content, order, type, media_ids } = bloc;
      bloc = { type, content, order, media_ids };
      // bloc.media_ids = [];
      bloc.id = id;

      formData.append('blocs[]', JSON.stringify(bloc));
    });

    return this.http
      .post(`${apiUrl}/articles_blocs?_method=PUT`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  deleteArticleBlock(block_id: any) {
    return this.http.delete(`${apiUrl}/articles_blocs/${block_id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // Gestion des alertes                                  //
  //                                                      //
  //////////////////////////////////////////////////////////

  getAlertes(city_id: any, page: any) {
    return this.http
      .get(`${apiUrl}/elu/cities/${city_id}/alerts?page=${page}&per_page=10`)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  postAlerte(cities: any, alerte: any) {
    const formData = new FormData();
    formData.append('title', alerte.title);
    formData.append('subtitle', alerte.subtitle);
    formData.append('description', alerte.description);
    formData.append('published_by', alerte.published_by);
    formData.append('published_at', alerte.published_at);
    formData.append('plan_for', alerte.plan_for);
    formData.append('sponsored', alerte.sponsored == true ? '1' : '0');

    // store in cities_id all the ids of the cities
    let cities_id: any = [];
    cities.forEach((city: any) => {
      cities_id.push(city.id);
    });

    cities_id.forEach((city_id: any) => {
      formData.append('cities_id[]', city_id);
    });

    return this.http.post(`${apiUrl}/alert`, formData).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getSingleAlerte(id: any) {
    return this.http.get(`${apiUrl}/elu/alerts/${id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  editAlerte(alerte: any) {
    const formData = new FormData();
    formData.append('title', alerte.title);
    formData.append('subtitle', alerte.subtitle);
    formData.append('description', alerte.description);
    formData.append('published_at', alerte.published_at);
    formData.append('plan_for', alerte.plan_for);
    formData.append('sponsored', alerte.sponsored == true ? '1' : '0');

    return this.http
      .post(`${apiUrl}/alerts/${alerte.id}?_method=PUT`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  deleteAlerte(alerte_id: any) {
    return this.http.delete(`${apiUrl}/alerts/${alerte_id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  linkCitiesToAlerte(alerte_id: any, cities: any) {
    const formData = new FormData();
    cities.forEach((city: any) => {
      formData.append('cities_id[]', city.id);
    });

    return this.http
      .post(`${apiUrl}/alerts/${alerte_id}/cities?_method=PUT`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  //////////////////////////////////////////////////////////
  // Gestion des city settings                            //
  //                                                      //
  //////////////////////////////////////////////////////////

  getCitySettings(city_id: any) {
    return this.http
      .get(`${apiUrl}/elu/cities/${city_id}/cities_settings`)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  uploadSettingFile(file: any, order: any) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('order', order);

    return this.http.post(`${apiUrl}/cities_settings/media`, formData).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  editSettingFile(file_id: any, order: any) {
    const formData = new FormData();
    formData.append('order', order);

    return this.http
      .put(`${apiUrl}/cities_settings/media/${file_id}`, formData)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  deleteSettingFile(file_id: any) {
    return this.http.delete(`${apiUrl}/cities_settings/media/${file_id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  updateCitySettings(settings: any, city_id: any) {
    const formData = new FormData();
    settings.forEach((setting: any) => {
      const { slug_setting, value, active, media_ids } = setting;
      setting = { slug_setting, value, active, media_ids };

      formData.append('settings[]', JSON.stringify(setting));
    });

    return this.http
      .post(
        `${apiUrl}/elu/cities/${city_id}/cities_settings?_method=PUT`,
        formData
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  //////////////////////////////////////////////////////////
  // Gestion des dommages réseaux                         //
  //                                                      //
  //////////////////////////////////////////////////////////

  listDommages(options?: { elu_id?: any; emailDemandeur?: any }) {
    let url = `${apiUrl}/${reseau}/dommages`;
    if (options && options.elu_id) {
      url = url + '?elu_id=' + options.elu_id;
    } else if (options && options.emailDemandeur) {
      url = url + '?emailDemandeur=' + options.emailDemandeur;
    }

    return this.http.get(url).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getDommage(dommage_id: any) {
    return this.http.get(`${apiUrl}/${reseau}/dommages/${dommage_id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getDommageLight(dommage_id: any) {
    return this.http.get(`${apiUrl}/${reseau}/dommages/${dommage_id}/light`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  dommageAddComment(ticket_id: any, description: any) {
    let form: FormData = new FormData();
    form.append('description', description);

    return this.http
      .post(`${apiUrl}/${reseau}/dommages/${ticket_id}/comment`, form)
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  dommageAddVieTicket(ticket_id: any, messageContent: any) {
    let form: FormData = new FormData();
    form.append('messageContent', messageContent);

    return this.http
      .post(`${apiUrl}/dommages/dommage_add_vie_ticket/${ticket_id}`, form)
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  gfuAddVieTicket(ticket_id: any, messageContent: any) {
    let form: FormData = new FormData();
    form.append('messageContent', messageContent);

    return this.http
      .post(`${apiUrl}/dommages/gfu_add_vie_ticket/${ticket_id}`, form)
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  dommageAddDocument(ticket_id: any, file: any) {
    let form: FormData = new FormData();
    form.set('fichiers[]', file, file.name);

    return this.http
      .post(`${apiUrl}/${reseau}/dommages/${ticket_id}/document`, form)
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  addDommage(
    emplacement: any,
    troubleDescription: any,
    emailDemandeur: any,
    nomDemandeur: any,
    prenomDemandeur: any,
    elu_id: any,
    insee: any,
    voie: any,
    numero: any,
    defectClassId: any,
    file: any,
    contactPhoneNumber1: any,
    contactPhoneNumber2: any,
    customerAccessibilityHours: any,
  ) {
    let url = `${apiUrl}/${reseau}/dommages`;

    let form: FormData = new FormData();
    form.set('emplacement', emplacement);
    form.set('troubleDescription', troubleDescription);
    form.set('emailDemandeur', emailDemandeur);
    form.set('nomDemandeur', nomDemandeur);
    form.set('prenomDemandeur', prenomDemandeur);
    form.set('elu_id', elu_id);
    form.set('insee', insee);
    form.set('voie', voie);
    form.set('numero', numero);
    form.set('defectClassId', defectClassId);

    form.set('contactPhoneNumber1', contactPhoneNumber1);
    if (contactPhoneNumber2) form.set('contactPhoneNumber2', contactPhoneNumber2);
    form.set('customerAccessibilityHours', customerAccessibilityHours);
    form.set('customerFirstName', prenomDemandeur);
    form.set('customerLastName', nomDemandeur);

    if (file.length) {
      file.forEach((f: any) => {
        form.append('fichiers[]', f, f.name);
      });
    }

    return this.http.post(url, form).pipe(
      map((data: any) => {
        return data;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // Add GFU                                              //
  //////////////////////////////////////////////////////////

  addGFU(
    troubleDescription: any,
    emailDemandeur: any,
    nomDemandeur: any,
    prenomDemandeur: any,
    telDemandeur: any,
    defectClass: any,
    elu_id: any,
    cls: any,
    insee: any,
    voie: any,
    numero: any,
    nomSite: any,
    contactPhoneNumber2: any,
    customerAccessibilityHours: any,
  ) {
    let url = `${apiUrl}/${reseau}/gfu`;

    let form: FormData = new FormData();
    form.set('troubleDescription', troubleDescription);
    form.set('emailDemandeur', emailDemandeur);
    form.set('nomDemandeur', nomDemandeur);
    form.set('prenomDemandeur', prenomDemandeur);
    form.set('telDemandeur', telDemandeur);
    form.set('defectClass', defectClass);
    form.set('elu_id', elu_id);
    form.set('cls', cls);
    form.set('insee', insee);
    form.set('voie', voie);
    form.set('numero', numero);
    form.set('nomSite', nomSite);

    form.set('contactPhoneNumber1', telDemandeur);
    if (contactPhoneNumber2) form.set('contactPhoneNumber2', contactPhoneNumber2);
    form.set('customerAccessibilityHours', customerAccessibilityHours);
    form.set('customerFirstName', prenomDemandeur);
    form.set('customerLastName', nomDemandeur);

    return this.http.post(url, form).pipe(
      map((data: any) => {
        return data;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // Add Documents à un GFU                               //
  //////////////////////////////////////////////////////////

  addGFUDocuments(gfu_id: any, files: any) {
    let form: FormData = new FormData();

    files.forEach((file: any) => {
      form.append('fichiers[]', file, file.name);
    });

    return this.http
      .post(`${apiUrl}/${reseau}/gfu/${gfu_id}/document`, form)
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  //////////////////////////////////////////////////////////
  // Get GFUS elements                                    //
  //////////////////////////////////////////////////////////

  gfus: any;
  gfusChanges: Subject<any> = new Subject<any>();

  getGFUsElements(elu_id: any, insee?: any) {
    const url = `${apiUrl}/${reseau}/gfus?elu_id=${elu_id}${insee ? `&insee=${insee}` : ''}`;

    return this.http.get(url).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // Get GFUS ids                                         //
  //////////////////////////////////////////////////////////

  getGFUsIds(elu_id: any, insee?: any) {
    const url = `${apiUrl}/${reseau}/gfu_tickets?elu_id=${elu_id}${insee ? `&insee=${insee}` : ''}`;

    return this.http.get(url).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // Get un GFU                                           //
  //////////////////////////////////////////////////////////

  gfusTickets: any;
  gfusTicketsChanges: Subject<any> = new Subject<any>();

  getGFU(gfu_id: any) {
    return this.http.get(`${apiUrl}/${reseau}/gfu/${gfu_id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // Get un GFU light                                           //
  //////////////////////////////////////////////////////////

  getGFUlight(gfu_id: any) {
    return this.http.get(`${apiUrl}/${reseau}/gfu/${gfu_id}/light`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // Gestion des RODP                                     //
  //////////////////////////////////////////////////////////

  getRodp(insee: any) {
    return this.http.get(`${apiUrl}/${reseau}/communes/${insee}/rodp`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getRodpPDF(insee: any, year: any) {
    return this.http
      .get(`${apiUrl}/${reseau}/communes/${insee}/rodp_pdf?annee=${year}`)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  //////////////////////////////////////////////////////////
  // Update password                                      //
  //////////////////////////////////////////////////////////

  resetPassword(email: any) {
    let form: FormData = new FormData();
    form.set('email', email);

    return this.http.post(`${apiUrl}/passwordrecovery`, form).pipe(
      map((data: any) => {
        return data;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // EPCI                                                 //
  //////////////////////////////////////////////////////////

  epciInfos: any;
  epciInfosChanges: Subject<any> = new Subject<any>();

  getEPCIInfos(epci_id: any) {
    return this.http.get(`${apiUrl}/epci_infos/${epci_id}`).pipe(
      map((res: any) => {
        this.epciInfos = res;
        this.epciInfosChanges.next(this.epciInfos);
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // Stats générales                                      //
  //////////////////////////////////////////////////////////

  generalStats: any;
  generalStatsChanges: Subject<any> = new Subject<any>();

  getGeneralStats() {
    return this.http.get(`${apiUrl}/stats`).pipe(
      map((res: any) => {
        this.generalStats = res;
        this.generalStatsChanges.next(this.generalStats);
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // Stats user, device, website                          //
  //////////////////////////////////////////////////////////

  statRegisterUser() {
    const elu = JSON.parse(localStorage.getItem('elu')!);
    const login = elu.login;
    const uuid = this.getUuid();

    let form: FormData = new FormData();
    form.set('email', login);
    form.set('uuid', uuid);

    this.http
      .post(`${apiUrl}/stats/addstatuser`, form)
      .subscribe((data: any) => {
        localStorage.setItem('stats_users_id', data.data.stats_users_id);
        this.statRegisterDevice(data.data.stats_users_id);
        return data;
      });
  }

  statRegisterDevice(statsUsersId: any) {
    let form: FormData = new FormData();
    form.set('stats_users_id', statsUsersId);
    form.set('ipv4', 'null');

    this.http
      .post(`${apiUrl}/stats/addstatdevice`, form)
      .subscribe((data: any) => {
        localStorage.setItem('stats_devices_id', data.data.stats_devices_id);
        this.addStatsWebsite('login');
      });
  }

  addStatsWebsite(title: any) {
    this.addStatsGeoloc(title, '48.533329', '7.63333', 'perso');
  }

  addStatsGeoloc(title: any, lat: any, lon: any, geoType: any) {
    const stats_devices_id = localStorage.getItem('stats_devices_id')!;

    let form: FormData = new FormData();
    form.set('title', title);
    form.set('lat', lat);
    form.set('lon', lon);
    form.set('geoType', geoType);
    form.set('stats_devices_id', stats_devices_id);
    form.set('websites_id', environment.project_id.toString());
    form.set('website_url', location.href);

    this.http
      .post(`${apiUrl}/stats/addstatwebsite`, form)
      .subscribe((data: any) => {
        return data;
      });
  }

  getUuid() {
    const uuid = localStorage.getItem('uuid');
    if (uuid) {
      return uuid;
    } else {
      const newUuid = this.uuidv4();
      localStorage.setItem('uuid', newUuid);
      return newUuid;
    }
  }

  uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        let r = (Math.random() * 16) | 0,
          v = c == 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  //////////////////////////////////////////////////////////
  // Call pour récupérer un JSON donnant la liste des     //
  // collectivités qui doivent avoir accès au             //
  // questionnaire sur /commune/:insee                    //
  //////////////////////////////////////////////////////////

  getFormJson() {
    // pageFormJsonId = 0 : questionnaire FUTÉ désactivé pour cette marque
    // (les pages appellent getForm() qui s'arrête si la réponse est vide)
    if (!environment.pageFormJsonId) {
      return of('');
    }
    return this.http
      .get(`${apiUrl}/${reseau}/page?page_id=${environment.pageFormJsonId}`)
      .pipe(
        map((res: any) => {
          return res.data.contenu;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  getFormEntries() {
    let url =
      'https://questionnaire.fute.fibre.services/wp-json/gf/v2/forms/6/entries?_field_ids=status,source_url,fg_easypassthrough_token';

    return this.http
      .get(url, {
        headers: new HttpHeaders({
          Authorization:
            'Basic ' + btoa(environment.logGF + ':' + environment.passGF),
        }),
      })
      .pipe(
        map((data: any) => {
          // this.storage.set('getFormEntries', data.entries);
          return data.entries;
        })
      );
  }

  //////////////////////////////////////////////////////////
  // Call pour récupérer les infos du dashboard cuivre    //
  //////////////////////////////////////////////////////////

  getDashCuivre(insee: any) {
    return this.http.get(`${apiUrl}/${reseau}/infos_cuivre/${insee}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // Call pour récupérer les infos des logements en       //
  // cliquant sur une prise dans la carte raccordement    //
  //////////////////////////////////////////////////////////

  getInfosImb(imb: any) {
    return this.http.get(`${apiUrl}/${reseau}/get_infos_imb?imb=${imb}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // Documentation cuivre                                 //
  //////////////////////////////////////////////////////////

  getDocumentationPageCuivre() {
    return this.http
      .get(
        `${apiUrl}/${reseau}/page?page_id=${environment.pageDocumentationCuivreId}`
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          throw new Error(error.message);
        })
      );
  }

  //////////////////////////////////////////////////////////
  // Récupérer la liste des villes si pwa_conseil_id      //
  //////////////////////////////////////////////////////////

  getConseilCities(conseil_id: any) {
    return this.http.get(`${apiUrl}/councilor/${conseil_id}/cities`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }


  //////////////////////////////////////////////////////////
  // Récupérer la home d'un departement                   //
  //////////////////////////////////////////////////////////

  departementInfos: any;
  departementInfosChanges: Subject<any> = new Subject<any>();

  getDepartementHome(departement_id: any) {
    // let baseUrl = apiUrl.split('/api')[0];
    // console.log('baseUrl', baseUrl);

    return this.http.get(`${apiUrl}/elu/get_home_departement?departement_id=${departement_id}`).pipe(
      map((res: any) => {
        this.departementInfos = res;
        this.departementInfosChanges.next(this.departementInfos);
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }


  getDepartementCities(departement_id: any) {
    return this.http.get(`${apiUrl}/elu/get_departement_cities?departement_id=${departement_id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getDepartementElus(departement_id: any) {
    return this.http.get(`${apiUrl}/departements/${departement_id}/elus`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  addDepartementElu(departement_id: any, elu: any) {
    const formData = new FormData();
    formData.append('login', elu.login);
    formData.append('nom', elu.nom);
    formData.append('prenom', elu.prenom);
    formData.append('email', elu.email);
    formData.append('dummy', elu.dummy);
    formData.append('nom_application', elu.nom_application);
    // formData.append('insee', elu.insee);
    formData.append('password', elu.password);
    formData.append('civilite', elu.civilite);
    formData.append('organisme', elu.organisme);
    formData.append('adresse', elu.adresse);
    // formData.append('cp', elu.cp);
    // formData.append('ville', elu.ville);
    formData.append('telephone', elu.telephone);

    return this.http.post(`${apiUrl}/departements/${departement_id}/elus?departement_id=${departement_id}`, formData).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }


  getPtoManquants(insee: any) {
    return this.http.get(`${apiUrl}/elu_ptomanquants?reseau=${reseau}&insee=${insee}`,
      { responseType: 'blob' }
    );
  }

  // TICKETS ELU

  getCommuneTickets(insee: any) {
    return this.http.get(`${apiUrl}/communes/${insee}/tickets`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getSingleTicket(ticket_id: any) {
    return this.http.get(`${apiUrl}/communes/tickets/${ticket_id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  editSingleTicket(ticket_message_id: any, toreview_at: any) {
    const formData = new FormData();
    formData.append('toreview_at', toreview_at);

    return this.http.post(`${apiUrl}/communes/ticket_messages/${ticket_message_id}?_method=PUT`, formData).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  createTicket(insee: any, ticket: any) {
    const formData = new FormData();

    // the form needs to have these fields: reseau, firstname, lastname, title, content, role
    formData.append('reseau', reseau);
    formData.append('firstname', ticket.firstname);
    formData.append('lastname', ticket.lastname);
    formData.append('title', ticket.title);
    formData.append('content', ticket.content);
    formData.append('role', ticket.role);
    formData.append('ticket_type_id', ticket.category);

    return this.http.post(`${apiUrl}/communes/${insee}/tickets`, formData).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  addMessageToTicketAsElu(ticket_id: any, message: any) {
    const formData = new FormData();
    formData.append('content', message.content);
    formData.append('parent_message_id', message.parent_message_id);
    formData.append('notify_direction', message.notify_direction);

    return this.http.post(`${apiUrl}/communes/tickets/${ticket_id}`, formData).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  closeTicketAsElu(ticket_id: any) {
    return this.http.post(`${apiUrl}/communes/tickets/${ticket_id}/close`, {
    }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getContactInfos(insee: any) {
    return this.http.get(`${apiUrl}/communes/${insee}/contact`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getTicketsAsAgent(
    insee: any,
    page: number,
    per_page: number,
    dept: string[] = [],
    statuses: number[] = [],
    filters: any = {}
  ) {
    let params = new HttpParams()
      .set('page', page)
      .set('per_page', per_page);

    if (insee) params = params.set('insee', insee);
    if (dept?.length > 0) params = params.set('dept', dept.join(','));
    if (statuses?.length > 0) params = params.set('status', statuses.join(','));

    // 🔥 Nouveaux paramètres API
    if (filters.ticket_number) params = params.set('ticket_number', filters.ticket_number);
    if (filters.title) params = params.set('title', filters.title);
    if (filters.name) params = params.set('name', filters.name);
    if (filters.ticket_type_id) params = params.set('ticket_type_id', filters.ticket_type_id);
    if (filters.last_message_from) params = params.set('last_message_from', filters.last_message_from);

    return this.http.get(`${apiUrl}/tickets`, { params }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }



  getSingleTicketAsAgent(ticket_id: any) {
    return this.http.get(`${apiUrl}/tickets/${ticket_id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  addMessageToTicketAsAgent(ticket_id: any, message: any, files: File[] = []) {
    const formData = new FormData();

    formData.append('content', message.content);
    formData.append('notify_manager', message.notify_manager);
    formData.append('notify_vdr', message.notify_vdr);
    formData.append('notify_direction', message.notify_direction);
    formData.append('sync_t0', message.sync_t0);
    formData.append('sync_database', message.sync_database);
    formData.append('is_summary', message.is_summary);
    formData.append('close_ticket', message.close_ticket);
    formData.append('is_correctedspelling', String(message.is_correctedspelling));
    formData.append('is_correctedstyle', String(message.is_correctedstyle));

    // ✅ Ajout dynamique des fichiers
    if (files && files.length > 0) {
      files.forEach((file, index) => {
        formData.append(`file${index + 1}`, file);
      });
    }

    return this.http.post(`${apiUrl}/tickets/${ticket_id}`, formData).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }



  closeTicketAsAgent(ticket_id: any, user_id: any) {
    return this.http.post(`${apiUrl}/tickets/${ticket_id}/close`, {
      user_id: user_id,
    }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  reopenTicketAsAgent(ticket_id: any) {
    return this.http.post(`${apiUrl}/tickets/${ticket_id}/reopen`, {}).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  spellcheck(content: any) {
    const formData = new FormData();
    formData.append('content', content);

    return this.http.post(`${apiUrl}/spellcheck`, formData).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  stylecheck(content: any) {
    const formData = new FormData();
    formData.append('content', content);

    return this.http.post(`${apiUrl}/stylecheck`, formData).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  uploadAgentPhoto(file: any, elu_id: any) {
    const formData = new FormData();
    formData.append('photo', file, file.name);

    return this.http.post(`${apiUrl}/elus/${elu_id}/photo`, formData).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getTicketTypes() {
    return this.http.get(`${apiUrl}/ticket_types`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  allStatuses: any = null;
  allStatusesChanges: Subject<any> = new Subject<any>();

  getTicketStatuses() {
    return this.http.get(`${apiUrl}/ticket_statuses`).pipe(
      map((res: any) => {
        this.allStatuses = res.data;
        this.allStatusesChanges.next(this.allStatuses);
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  getDossierResume(id: any) {
    return this.http.get(`${apiUrl}/dossier_resume?id=${id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        throw new Error(error.message);
      })
    );
  }

  //////////////////////////////////////////////////////////
  // CRM v2 — Types d'action d'échange + templates email  //
  // (company_id dérivé du reseau : losange=2, rosace=1)  //
  //////////////////////////////////////////////////////////

  private get ficheDecouverteCompanyId(): number {
    return environment.reseau === 'losange' ? 2 : 1;
  }

  /** Liste paginée des types d'action d'échange de la company courante. */
  getEchangeActions(params: { page?: number; per_page?: number; search?: string } = {}) {
    let query = new HttpParams()
      .set('company_id', String(this.ficheDecouverteCompanyId))
      .set('page', String(params.page ?? 1))
      .set('per_page', String(params.per_page ?? 20));
    if (params.search && params.search.trim().length) {
      query = query.set('search', params.search.trim());
    }
    return this.http.get(`${apiUrl}/v2/fiche-decouverte-actions`, { params: query }).pipe(
      map((res: any) => res),
      catchError((error: any) => { throw new Error(error.message); })
    );
  }

  /** Détail d'un type d'action (avec PJ, médias, versions, éditeur). */
  getEchangeAction(id: number) {
    return this.http.get(`${apiUrl}/v2/fiche-decouverte-actions/${id}/show`).pipe(
      map((res: any) => res),
      catchError((error: any) => { throw new Error(error.message); })
    );
  }

  /** Shortcodes disponibles (rendus côté serveur via {{entity.attr}}). */
  getEchangeShortcodes(category?: string) {
    let params = new HttpParams();
    if (category) params = params.set('category', category);
    return this.http.get(`${apiUrl}/v2/fiche-decouverte-actions/shortcodes`, { params }).pipe(
      map((res: any) => res),
      catchError((error: any) => { throw new Error(error.message); })
    );
  }

  /** Historique des versions d'un template email. */
  getEchangeActionVersions(id: number) {
    return this.http.get(`${apiUrl}/v2/fiche-decouverte-actions/${id}/versions`).pipe(
      map((res: any) => res),
      catchError((error: any) => { throw new Error(error.message); })
    );
  }

  /**
   * Met à jour le template email d'un type d'action.
   * `html` est envoyé comme un FICHIER (règle backend file + mimetypes text/html).
   * `design` est envoyé comme une chaîne JSON (email_body_design).
   */
  updateEchangeActionEmailTemplate(
    id: number,
    payload: {
      email_subject: string;
      name?: string;
      slug?: string;
      email_from_name?: string;
      email_reply_to?: string;
      email_is_editable?: boolean;
      email_locale?: string;
      join_pto_manquants?: boolean;
      html: string;
      design: any;
    }
  ) {
    const fd = new FormData();
    fd.append('company_id', String(this.ficheDecouverteCompanyId));
    fd.append('email_subject', payload.email_subject);
    if (payload.name != null) fd.append('name', payload.name);
    if (payload.slug != null) fd.append('slug', payload.slug);
    if (payload.email_from_name != null) fd.append('email_from_name', payload.email_from_name);
    if (payload.email_reply_to != null) fd.append('email_reply_to', payload.email_reply_to);
    if (payload.email_is_editable != null) fd.append('email_is_editable', payload.email_is_editable ? '1' : '0');
    if (payload.email_locale != null) fd.append('email_locale', payload.email_locale);
    if (payload.join_pto_manquants != null) fd.append('join_pto_manquants', payload.join_pto_manquants ? '1' : '0');
    // Corps HTML = fichier (Blob nommé, Content-Type text/html)
    fd.append('email_body_html', new Blob([payload.html], { type: 'text/html' }), 'body.html');
    // Design = JSON string
    fd.append('email_body_design', JSON.stringify(payload.design));
    return this.http.post(`${apiUrl}/v2/fiche-decouverte-actions/email-template/${id}`, fd).pipe(
      map((res: any) => res),
      catchError((error: any) => { throw new Error(error.message); })
    );
  }

  /** Aperçu rendu (shortcodes résolus) pour un élu donné. */
  previewEchangeActionEmail(id: number, elu_id: number, user_id?: number) {
    const body: any = { elu_id };
    if (user_id != null) body.user_id = user_id;
    return this.http.post(`${apiUrl}/v2/fiche-decouverte-actions/email-template/preview/${id}`, body).pipe(
      map((res: any) => res),
      catchError((error: any) => { throw new Error(error.message); })
    );
  }

  /** Envoi d'un email de test (destinataire forcé côté serveur hors prod). */
  testSendEchangeActionEmail(id: number, body: { elu_id?: number; to?: string }) {
    return this.http.post(`${apiUrl}/v2/fiche-decouverte-actions/email-template/test-send/${id}`, body).pipe(
      map((res: any) => res),
      catchError((error: any) => { throw new Error(error.message); })
    );
  }

  /** Restaure une version archivée du template email. */
  restoreEchangeActionVersion(id: number, version: number) {
    return this.http.post(`${apiUrl}/v2/fiche-decouverte-actions/${id}/versions/${version}/restore`, {}).pipe(
      map((res: any) => res),
      catchError((error: any) => { throw new Error(error.message); })
    );
  }

  /** Ajoute une pièce jointe (pdf, doc, img, zip… ≤ 10 Mo). */
  uploadEchangeActionAttachment(id: number, file: File) {
    const fd = new FormData();
    fd.append('company_id', String(this.ficheDecouverteCompanyId));
    fd.append('file', file);
    return this.http.post(`${apiUrl}/v2/fiche-decouverte-actions/${id}/attachment`, fd).pipe(
      map((res: any) => res),
      catchError((error: any) => { throw new Error(error.message); })
    );
  }

  /** Ajoute un média image (≤ 5 Mo). Renvoie l'objet média avec public_url. */
  uploadEchangeActionMedia(id: number, file: File, alt_text: string) {
    const fd = new FormData();
    fd.append('company_id', String(this.ficheDecouverteCompanyId));
    fd.append('file', file);
    fd.append('alt_text', alt_text);
    return this.http.post(`${apiUrl}/v2/fiche-decouverte-actions/${id}/media`, fd).pipe(
      map((res: any) => res),
      catchError((error: any) => { throw new Error(error.message); })
    );
  }

  /**
   * Crée un nouveau type d'action d'échange.
   * POST /v2/fiche-decouverte-actions?company_id=…&name=…
   * Renvoie l'action créée (avec son id).
   */
  createEchangeAction(name: string) {
    const params = new HttpParams()
      .set('company_id', String(this.ficheDecouverteCompanyId))
      .set('name', name);
    return this.http
      .post(`${apiUrl}/v2/fiche-decouverte-actions`, {}, { params })
      .pipe(
        map((res: any) => res),
        catchError((error: any) => { throw new Error(error.message); })
      );
  }

  /**
   * Duplique un type d'action d'échange (template email, PJ, médias…).
   * POST /v2/fiche-decouverte-actions/{id}/duplicate
   * Renvoie l'action nouvellement créée (avec son id).
   */
  duplicateEchangeAction(id: number) {
    return this.http.post(`${apiUrl}/v2/fiche-decouverte-actions/${id}/duplicate`, {}).pipe(
      map((res: any) => res),
      catchError((error: any) => { throw new Error(error.message); })
    );
  }
}
